import { Grid, Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFnsV3";
import { Provider } from "react-redux";
import { Outlet, useParams } from "react-router";
import AuthenticationFailed from "../shared/components/AuthenticationFailed";
import ErrorBoundary from "../shared/components/ErrorBoundary";
import FullScreenLoader from "../shared/components/fullScreenLoader/FullScreenLoader";
import { ClientPermissionsContextProvider } from "../shared/contexts/ClientPermissionsContextProvider";
import { ClientInfo } from "../shared/reporting/api/biClient.types";
import { getDefaultBiDateLocale } from "../shared/reporting/components/viewer/common/bc-datepicker/helpers/bcDatePickerHelper";
import { AppInitializer } from "./components/AppInitializer";
import IntercomSettingsProvider from "./components/builder/IntercomSettingsProvider";
import ImportantDataLoadingFailed from "./components/common/ImportantDataLoadingFailed";
import { ClientContextProvider } from "./contexts/ClientContextProvider";
import { UserContextProvider } from "./contexts/UserContextProvider";
import { useClientManagement } from "./hooks/useClientManagement";
import { useClientNavigation } from "./hooks/useClientNavigation";
import { useLocalization } from "./hooks/useLocalization";
import { useUserAuthentication } from "./hooks/useUserAuthentication";
import { store } from "./store/store";

export default function App() {
  const { client } = useParams();
  const locale = useLocalization();

  const { isAuthenticated, user, loadingUserError, userHasNoPermissions } = useUserAuthentication();
  const { clientCode, setClientCode, clientInfo, clients } = useClientManagement(client);
  const { setCurrentClientCode } = useClientNavigation(client, user, setClientCode, clients);

  if (!isAuthenticated) {
    return <FullScreenLoader title={locale.redirect.title} subtitle={locale.redirect.subtitle} />;
  }

  if (userHasNoPermissions) {
    return <AuthenticationFailed />;
  }

  if (loadingUserError) {
    return (
      <ImportantDataLoadingFailed
        title={locale.common.report_data_loading_failed_title}
        disclaimer={locale.common.report_data_loading_failed_text}
      />
    );
  }

  if (user === undefined || clientCode === undefined || clients === undefined) {
    return <FullScreenLoader title={locale.identity.title} subtitle={locale.identity.subtitle} />;
  }

  return (
    <ErrorBoundary>
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getDefaultBiDateLocale()}>
        <Provider store={store}>
          <AppInitializer clientCode={clientCode}>
            <UserContextProvider value={user}>
              <IntercomSettingsProvider />
              <ClientContextProvider
                client={{
                  clientCode,
                  setClientCode: setCurrentClientCode,
                  clientInfo: clientInfo as ClientInfo,
                  clients: clients,
                }}
              >
                <ClientPermissionsContextProvider
                  permissions={user.permissions.find((p) => p.clientCode === clientCode)?.permissions || []}
                  permissionSets={user.permissions}
                >
                  <Stack flex={1}>
                    <Grid container flex={1} maxHeight={"100%"} width={"100%"}>
                      <Outlet />
                    </Grid>
                  </Stack>
                </ClientPermissionsContextProvider>
              </ClientContextProvider>
            </UserContextProvider>
          </AppInitializer>
        </Provider>
      </LocalizationProvider>
    </ErrorBoundary>
  );
}
